import { useEffect, useState } from "react";
import { m } from "framer-motion";
import { ArrowLeft, CalendarCheck, Check, Images, Play, Users } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BookingSearch } from "../components/BookingSearch";
import { Lightbox } from "../components/Lightbox";
import { HeritageOrnament } from "../components/HeritageOrnament";
import { SEO } from "../components/SEO";
import { getApartment } from "../data/apartments";
import { useReveal } from "../hooks/useReveal";
import { money } from "../utils/routes";
import { NotFound } from "./NotFound";

function youtubeEmbedUrl(videoId: string) {
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    controls: "1",
    rel: "0",
    modestbranding: "1",
    iv_load_policy: "3",
  });

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

export function ApartmentDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const apartment = getApartment(slug);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [videoActive, setVideoActive] = useState(false);
  const galleryImages = apartment ? Array.from(new Set([apartment.featuredImage, ...apartment.gallery])) : [];
  const galleryPreviewImages = galleryImages.slice(0, 6);
  const detailTitle = apartment ? apartment.detailTitle ?? apartment.title : "";
  const videoUrl = apartment?.youtubeVideoId ? youtubeEmbedUrl(apartment.youtubeVideoId) : undefined;
  const mediaLabel = videoUrl ? "Video tour" : apartment?.status === "non-bookable" ? "Inquiry preview" : "Photo tour";
  const mediaAriaLabel = videoUrl ? `${detailTitle} video tour` : `${detailTitle} photo preview`;

  const reveal = useReveal();

  const openViewer = (index: number) => {
    setSelectedImageIndex(index);
    setViewerOpen(true);
  };

  useEffect(() => {
    setSelectedImageIndex(0);
    setViewerOpen(false);
    setVideoActive(false);
  }, [apartment?.slug]);

  if (!apartment) {
    return <NotFound />;
  }

  return (
    <>
      <SEO
        title={`${apartment.title} | The Deposit Seed House`}
        description={apartment.summary}
        image={apartment.featuredImage}
        canonicalPath={`/apartments/${apartment.slug}`}
      />
      <section className="apartment-video-hero">
        <div className="apartment-video-hero__media" aria-label={mediaAriaLabel}>
          {videoUrl && videoActive ? (
            <iframe
              src={videoUrl}
              title={`${detailTitle} video tour`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : videoUrl ? (
            <button className="apartment-video-hero__poster" type="button" onClick={() => setVideoActive(true)} aria-label={`Play ${detailTitle} video tour`}>
              <img src={apartment.featuredImage} alt={apartment.alt} />
              <span>
                <Play size={22} aria-hidden="true" />
                Play video tour
              </span>
            </button>
          ) : (
            <button
              className="apartment-video-hero__poster apartment-video-hero__poster--photo"
              type="button"
              onClick={() => openViewer(0)}
              aria-label={`View ${detailTitle} photos`}
            >
              <img src={apartment.featuredImage} alt={apartment.alt} />
              <span>
                <Images size={22} aria-hidden="true" />
                View photos
              </span>
            </button>
          )}
        </div>
        <m.div className="apartment-video-hero__panel" {...reveal}>
          <Link className="text-link text-link--back" to="/apartments">
            <ArrowLeft size={16} aria-hidden="true" />
            Apartments
          </Link>
          <div className="apartment-kicker">
            <span>{apartment.category}</span>
            <span>
              {videoUrl ? <Play size={14} aria-hidden="true" /> : <Images size={14} aria-hidden="true" />}
              {mediaLabel}
            </span>
          </div>
          <h1>{detailTitle}</h1>
          <HeritageOrnament className="section-ornament" />
          <p>{apartment.summary}</p>
          <div className="detail-facts detail-facts--hero">
            {apartment.priceNight && <strong>{money(apartment.priceNight)}/night</strong>}
            {apartment.sleeps && (
              <span>
                <Users size={18} aria-hidden="true" />
                Sleeps {apartment.sleeps}
              </span>
            )}
            <span>{apartment.bathrooms}</span>
            <span>{apartment.kitchen}</span>
          </div>
          {apartment.detailNote && <p className="status-note">{apartment.detailNote}</p>}
          <div className="apartment-hero-actions">
            {apartment.status !== "non-bookable" && (
              <a className="button button--primary" href="#booking-calendar">
                <CalendarCheck size={18} aria-hidden="true" />
                {apartment.reserveLabel}
              </a>
            )}
            <button className="button button--light" type="button" onClick={() => openViewer(0)}>
              <Images size={18} aria-hidden="true" />
              View photos
            </button>
          </div>
        </m.div>
      </section>
      <section className="section apartment-detail-section">
        <div className="apartment-detail-grid">
          <main className="apartment-detail-main">
            <m.section className="apartment-gallery-panel" aria-labelledby="apartment-gallery-title" {...reveal}>
              <div className="apartment-gallery-panel__top">
                <div className="apartment-section-heading">
                  <p className="eyebrow">Residence gallery</p>
                  <h2 id="apartment-gallery-title">Explore the space</h2>
                </div>
                {galleryImages.length > galleryPreviewImages.length && (
                  <button className="button button--light apartment-gallery-panel__action" type="button" onClick={() => openViewer(0)}>
                    <Images size={18} aria-hidden="true" />
                    View all {galleryImages.length} photos
                  </button>
                )}
              </div>
              <div className={`apartment-image-grid apartment-image-grid--c${galleryPreviewImages.length}`}>
                {galleryPreviewImages.map((image, index) => {
                  const showMoreOverlay = index === galleryPreviewImages.length - 1 && galleryImages.length > galleryPreviewImages.length;

                  return (
                    <button
                      className={`apartment-image-tile ${showMoreOverlay ? "apartment-image-tile--more" : ""}`}
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => openViewer(showMoreOverlay ? 0 : index)}
                      aria-label={showMoreOverlay ? `Open full ${detailTitle} photo gallery, ${galleryImages.length} photos` : `Open ${detailTitle} photo ${index + 1} of ${galleryImages.length}`}
                    >
                      <img src={image} alt="" loading="eager" />
                      {showMoreOverlay && (
                        <span className="apartment-image-tile__more">
                          <Images size={22} aria-hidden="true" />
                          <strong>View all photos</strong>
                          <small>{galleryImages.length} total</small>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </m.section>
            <m.section className="apartment-copy-panel prose" {...reveal}>
              <p className="eyebrow">Details</p>
              <h2>{apartment.descriptionHeading}</h2>
              {apartment.description.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <div className="spec-grid">
                <div>
                  <span>Sleeping</span>
                  <strong>{apartment.beds}</strong>
                </div>
                <div>
                  <span>Bathroom</span>
                  <strong>{apartment.bathrooms}</strong>
                </div>
                <div>
                  <span>Kitchen</span>
                  <strong>{apartment.kitchen}</strong>
                </div>
              </div>
              <h2>Amenities</h2>
              <ul className="check-list">
                {apartment.amenities.map((amenity) => (
                  <li key={amenity}>
                    <Check size={17} aria-hidden="true" />
                    {amenity}
                  </li>
                ))}
              </ul>
            </m.section>
          </main>
          <aside className="booking-panel apartment-booking-panel" id="booking-calendar">
            <h2>{apartment.status === "non-bookable" ? "Questions?" : "Ready to book?"}</h2>
            <p>{apartment.status === "non-bookable" ? "Contact us for more information." : "When would you like to stay?"}</p>
            {apartment.status !== "non-bookable" ? (
              <BookingSearch key={`${apartment.slug}${location.search}`} compact apartmentSlug={apartment.slug} />
            ) : (
              <Link className="button button--secondary button--full" to="/contact">
                {apartment.reserveLabel}
              </Link>
            )}
          </aside>
        </div>
      </section>
      <Lightbox
        images={galleryImages.map((src, index) => ({ src, alt: `${detailTitle} photo ${index + 1}` }))}
        index={selectedImageIndex}
        open={viewerOpen}
        title={detailTitle}
        onClose={() => setViewerOpen(false)}
        onIndexChange={setSelectedImageIndex}
      />
    </>
  );
}
